import * as React from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm, useFormState, useWatch } from 'react-hook-form';
import {
  AutocompleteMultiSelect,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  Input,
  Label,
  RadioGroup,
  RadioGroupItem,
  Textarea,
  toast,
} from '@shigo-ui/ui';

import { showRequestError } from '../../../../../lib/requestErrorToast';
import { useBusyDialogGuard } from '../../../../../hooks/useBusyDialogGuard';
import { dashboardT } from '../../../../../lib/messages';
import {
  blockedCreativeFormSchema,
  blockedCreativeInitialValue,
  serializeBlockedCreativeForm,
  type BlockedCreativeFormValue,
} from '../../blockedCreativeFormSchema';
import {
  useBlockedCreativeQuery,
  useBlockedListMutations,
  useBlockedListPublishersQuery,
} from '../../hooks';
import { blockedReasonLabel, BlockReasonCreative, type BlockedCreative } from '../../model';

interface BlockedCreativeDialogProps {
  creative: BlockedCreative | null;
  onClose: () => void;
}

const reasonOptions = Object.values(BlockReasonCreative);

export function BlockedCreativeDialog({ creative, onClose }: BlockedCreativeDialogProps) {
  // React Compiler memoization conflicts with this react-hook-form Controller/useWatch tree.
  'use no memo';

  const detail = useBlockedCreativeQuery(creative?.id);
  const current = detail.data ?? creative;
  const publishers = useBlockedListPublishersQuery(true);
  const mutations = useBlockedListMutations();
  const pending = mutations.create.isPending || mutations.update.isPending;
  const guard = useBusyDialogGuard(pending, onClose);
  const form = useForm<BlockedCreativeFormValue>({
    defaultValues: blockedCreativeInitialValue(current),
    resolver: zodResolver(blockedCreativeFormSchema),
  });
  const { errors, isDirty } = useFormState({ control: form.control });
  const blockAll = useWatch({ control: form.control, name: 'blockAll' });
  const resetCreativeId = React.useRef(current?.id ?? null);

  React.useEffect(() => {
    const currentId = current?.id ?? null;
    if (resetCreativeId.current !== currentId || !isDirty) {
      form.reset(blockedCreativeInitialValue(current));
      resetCreativeId.current = currentId;
    }
  }, [current, form, isDirty]);

  const publisherOptions = React.useMemo(() => {
    const options = [...(current?.publishers ?? []), ...(publishers.data ?? [])].map(
      (publisher) => ({ id: publisher.id, label: publisher.name }),
    );
    return Array.from(new Map(options.map((option) => [option.id, option])).values());
  }, [current?.publishers, publishers.data]);

  async function submit(value: BlockedCreativeFormValue) {
    try {
      const payload = serializeBlockedCreativeForm(value, current);
      if (current) await mutations.update.mutateAsync({ id: current.id, payload });
      else await mutations.create.mutateAsync(payload);
      toast.success(
        dashboardT(current ? 'blockedList.updatedMessage' : 'blockedList.createdMessage'),
      );
      onClose();
    } catch (error) {
      form.setError('root.server', { message: dashboardT('blockedList.saveError') });
      showRequestError(error, dashboardT('blockedList.saveError'));
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        guard.onOpenChange(open);
      }}
    >
      <DialogContent
        className="max-w-[620px] gap-0 overflow-visible p-0"
        onEscapeKeyDown={(event) => {
          guard.onDismissInteraction(event);
        }}
        onPointerDownOutside={(event) => {
          guard.onDismissInteraction(event);
        }}
      >
        <DialogHeader className="border-border border-b px-5 py-3.5">
          <DialogTitle className="text-[15px] font-semibold">
            {dashboardT(current ? 'blockedList.edit' : 'blockedList.add')}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {dashboardT(current ? 'blockedList.edit' : 'blockedList.add')}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form noValidate onSubmit={(event) => void form.handleSubmit(submit)(event)}>
            <div className="max-h-[70vh] space-y-5 overflow-y-auto px-5 py-4">
              {detail.isError ? (
                <p role="alert" className="text-destructive text-sm">
                  {dashboardT('blockedList.detailLoadError')}
                </p>
              ) : null}
              <div className="space-y-1.5">
                <Label htmlFor="blocked-creative-id" className="text-[13px] font-semibold">
                  {dashboardT('blockedList.creative')} <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="blocked-creative-id"
                  required
                  aria-invalid={Boolean(errors.creative)}
                  placeholder={dashboardT('blockedList.creativePlaceholder')}
                  {...form.register('creative')}
                />
                {errors.creative?.message ? (
                  <p role="alert" className="text-destructive text-xs">
                    {errors.creative.message}
                  </p>
                ) : null}
              </div>

              <div className="border-input rounded-md border p-3">
                <div className="grid grid-cols-[1rem_minmax(0,1fr)] items-center gap-x-3 gap-y-1">
                  <Controller
                    control={form.control}
                    name="blockAll"
                    render={({ field }) => (
                      <Checkbox
                        id="blocked-creative-all-publishers"
                        className="self-center"
                        checked={field.value}
                        onCheckedChange={(checked) => {
                          field.onChange(checked === true);
                          if (checked) form.setValue('publisherIds', [], { shouldDirty: true });
                        }}
                      />
                    )}
                  />
                  <Label className="self-center" htmlFor="blocked-creative-all-publishers">
                    {dashboardT('blockedList.allPublishers')}
                  </Label>
                  <p className="text-muted-foreground col-start-2 text-sm">
                    {dashboardT('blockedList.allPublishersDescription')}
                  </p>
                </div>
              </div>

              {!blockAll ? (
                <div className="space-y-1.5">
                  <Label className="text-[13px] font-semibold">
                    {dashboardT('blockedList.publishers')}
                  </Label>
                  <Controller
                    control={form.control}
                    name="publisherIds"
                    render={({ field }) => (
                      <AutocompleteMultiSelect
                        label={dashboardT('blockedList.publishers')}
                        options={publisherOptions}
                        value={field.value}
                        onValueChange={field.onChange}
                        loading={publishers.isLoading}
                        disabled={publishers.isError}
                        emptyMessage={dashboardT(
                          publishers.isError
                            ? 'blockedList.publisherLookupError'
                            : 'blockedList.publisherLookupEmpty',
                        )}
                        placeholder={dashboardT('blockedList.publishers')}
                      />
                    )}
                  />
                </div>
              ) : null}

              <fieldset className="space-y-2">
                <legend className="text-[13px] font-semibold">
                  {dashboardT('blockedList.reason')}
                </legend>
                <Controller
                  control={form.control}
                  name="reason"
                  render={({ field }) => (
                    <RadioGroup
                      value={field.value}
                      onValueChange={field.onChange}
                      className="grid gap-2 sm:grid-cols-2"
                    >
                      {reasonOptions.map((reason) => {
                        const id = `blocked-reason-${reason || 'none'}`;
                        return (
                          <div key={reason} className="flex items-center gap-2">
                            <RadioGroupItem id={id} value={reason} />
                            <Label htmlFor={id}>{dashboardT(blockedReasonLabel(reason))}</Label>
                          </div>
                        );
                      })}
                    </RadioGroup>
                  )}
                />
              </fieldset>

              <div className="space-y-1.5">
                <Label htmlFor="blocked-creative-comment" className="text-[13px] font-semibold">
                  {dashboardT('blockedList.comment')}
                </Label>
                <Textarea
                  id="blocked-creative-comment"
                  placeholder={dashboardT('blockedList.commentPlaceholder')}
                  {...form.register('comment')}
                />
              </div>
              {errors.root?.server?.message ? (
                <p role="alert" className="text-destructive text-sm">
                  {errors.root.server.message}
                </p>
              ) : null}
            </div>
            <DialogFooter className="border-border border-t px-5 py-3">
              <Button
                type="button"
                variant="outline"
                disabled={pending}
                onClick={() => {
                  guard.close();
                }}
              >
                {dashboardT('blockedList.cancel')}
              </Button>
              <Button type="submit" disabled={pending || detail.isLoading}>
                {dashboardT(pending ? 'blockedList.saving' : 'blockedList.save')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
